import { useState, useEffect, useRef } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, FileText, Search, ChevronLeft, ChevronRight, Plus, Eye, Edit, Calendar, User, Filter, X, CheckCircle, XCircle, Check, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import useStickyState from '../lib/useStickyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Authorized approvers - simplified
// Approvers by form type
const APPROVERS = {
  timesheet: {
    admin: ['b.deering@rogerstrucking.ca'],
    ar: ['j.creamer@rogerstrucking.ca']
  },
  shop: {
    admin: ['lring@rogerstrucking.ca'],
    ar: ['j.creamer@rogerstrucking.ca']
  }
};

export default function FormLogPage() {
  const { templateId } = useParams();
  const { user, loading: authLoading } = useAuth();
  
  const [template, setTemplate] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filter / pagination state — persisted per-template in sessionStorage so
  // navigating to a submission detail view and pressing Back preserves the
  // user's narrowed-down list.
  const k = (name) => `formlog:${templateId}:${name}`;
  const [page, setPage] = useStickyState(k('page'), 1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useStickyState(k('search'), '');
  const [dateFilter, setDateFilter] = useStickyState(k('dateFilter'), 'all'); // all, today, week, month, year, custom
  const [customDateStart, setCustomDateStart] = useStickyState(k('customDateStart'), '');
  const [customDateEnd, setCustomDateEnd] = useStickyState(k('customDateEnd'), '');
  const [selectedMonth, setSelectedMonth] = useStickyState(k('selectedMonth'), '');
  const [selectedYear, setSelectedYear] = useStickyState(k('selectedYear'), '');
  const [employeeFilter, setEmployeeFilter] = useStickyState(k('employeeFilter'), '');  // employee dropdown value
  const [lastNameFilter, setLastNameFilter] = useStickyState(k('lastNameFilter'), '');  // free-text last-name search
  const [allEmployees, setAllEmployees] = useState([]);  // dropdown options across ALL pages
  const perPage = 25;
  
  // Get current user email directly from auth context - wait for auth to load
  const currentUserEmail = user?.email?.toLowerCase() || '';
  
  // Debug: log auth state
  useEffect(() => {
    console.log('Auth state - loading:', authLoading, 'user:', user, 'email:', currentUserEmail);
  }, [authLoading, user, currentUserEmail]);

  // Reset to page 1 whenever filters change, otherwise we may land on an empty page.
  // Skip the first run so restored sessionStorage state isn't immediately clobbered.
  const isFirstFilterRun = useRef(true);
  useEffect(() => {
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }
    setPage(1);
  }, [dateFilter, customDateStart, customDateEnd, selectedMonth, selectedYear, employeeFilter, lastNameFilter]);

  useEffect(() => {
    fetchData();
  }, [templateId, page, dateFilter, customDateStart, customDateEnd, selectedMonth, selectedYear, employeeFilter, lastNameFilter]);

  // Fetch full employee list for dropdown (template-scoped, distinct, server-side)
  useEffect(() => {
    if (!templateId) return;
    const token = localStorage.getItem('vaultai_token');
    axios.get(`${API}/forms/submissions/distinct-employees?template_id=${templateId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setAllEmployees(res.data?.employees || []);
    }).catch(() => setAllEmployees([]));
  }, [templateId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch template info
      const templateRes = await axios.get(`${API}/forms/templates/${templateId}`, { headers });
      setTemplate(templateRes.data);

      // Build query params; pass user-entered date range when active so backend filters by data.date
      const params = new URLSearchParams({
        template_id: templateId,
        page: String(page),
        per_page: String(perPage),
      });
      
      // Compute data.date range from the active date filter
      let dStart = '', dEnd = '';
      if (dateFilter === 'custom') {
        if (customDateStart) dStart = customDateStart;
        if (customDateEnd) dEnd = customDateEnd;
      } else if (dateFilter === 'month' && selectedMonth && selectedYear) {
        const y = parseInt(selectedYear);
        const m = parseInt(selectedMonth);
        dStart = `${y}-${String(m).padStart(2, '0')}-01`;
        const lastDay = new Date(y, m, 0).getDate();
        dEnd = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      } else if (dateFilter === 'year' && selectedYear) {
        dStart = `${selectedYear}-01-01`;
        dEnd = `${selectedYear}-12-31`;
      } else if (dateFilter === 'today') {
        const todayBC = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Vancouver' }).format(new Date());
        dStart = todayBC;
        dEnd = todayBC;
      } else if (dateFilter === 'week') {
        const todayBC = new Date(new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Vancouver' }).format(new Date()));
        const weekAgo = new Date(todayBC);
        weekAgo.setDate(weekAgo.getDate() - 7);
        dStart = weekAgo.toISOString().slice(0, 10);
        dEnd = todayBC.toISOString().slice(0, 10);
      }
      if (dStart) params.set('data_date_start', dStart);
      if (dEnd) params.set('data_date_end', dEnd);
      
      // Pass employee/last-name filters to backend so pagination respects them
      if (employeeFilter) params.set('employee_name', employeeFilter);
      if (lastNameFilter && lastNameFilter.trim()) params.set('last_name_filter', lastNameFilter.trim());

      // Fetch submissions for this template
      const res = await axios.get(
        `${API}/forms/submissions?${params.toString()}`,
        { headers }
      );
      setEntries(res.data.submissions || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch form data:', err);
      toast.error('Failed to load form log');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Handle YYYY-MM-DD format to avoid timezone issues
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    // Display in BC (Pacific) timezone since that's where the team works
    return new Date(dateStr).toLocaleString('en-CA', {
      timeZone: 'America/Vancouver',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const totalPages = Math.ceil(total / perPage);
  
  // Members (non-admin/manager) should not see admin/AR approval columns
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
  const isAdmin = user?.role === 'admin';
  
  // Check if this is a timesheet form
  const templateNameLower = template?.name?.toLowerCase() || '';
  const isTimesheet = templateNameLower.includes('timesheet') || templateNameLower.includes('time sheet');
  const isShopTimesheet = templateNameLower.includes('shop') && (templateNameLower.includes('timesheet') || templateNameLower.includes('time sheet'));
  const isTimeOff = templateNameLower.includes('time-off');
  // "Reviewed" checkbox — show on every form EXCEPT timesheets (which have
  // their own Admin/AR approval workflow) and time-off (which has approve/deny).
  const showReviewCheckbox = !isTimesheet && !isTimeOff;
  
  // Get form type for approver checking
  const getFormType = () => {
    if (isShopTimesheet) return 'shop';
    if (isTimesheet) return 'timesheet';
    return null;
  };
  
  // Simple approval check - check email based on form type
  const canApproveAdmin = () => {
    const email = currentUserEmail?.toLowerCase() || '';
    const formType = getFormType();
    if (!formType) return false;
    return APPROVERS[formType]?.admin?.includes(email) || false;
  };
  
  const canApproveAR = () => {
    const email = currentUserEmail?.toLowerCase() || '';
    const formType = getFormType();
    if (!formType) return false;
    return APPROVERS[formType]?.ar?.includes(email) || false;
  };
  
  // Handle approval
  const handleApprove = async (submissionId, approvalType) => {
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.post(
        `${API}/forms/submissions/${submissionId}/approve?approval_type=${approvalType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${approvalType.toUpperCase()} approval added`);
      fetchData(); // Refresh data
    } catch (err) {
      console.error('Approval failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };
  
  // Handle revoke approval
  const handleRevokeApproval = async (submissionId, approvalType) => {
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.delete(
        `${API}/forms/submissions/${submissionId}/approve?approval_type=${approvalType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${approvalType.toUpperCase()} approval revoked`);
      fetchData(); // Refresh data
    } catch (err) {
      console.error('Revoke failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to revoke approval');
    }
  };

  // Time-Off approve/deny (admin only)
  const handleTimeOffDecision = async (submissionId, decision) => {
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.post(
        `${API}/forms/submissions/${submissionId}/time-off-decision?decision=${decision}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Time-off request ${decision === 'approve' ? 'approved' : 'denied'}`);
      fetchData();
    } catch (err) {
      console.error('Time-off decision failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to update time-off request');
    }
  };

  // Delete a submission (admin only)
  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Delete this entry permanently? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.delete(`${API}/forms/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      fetchData();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // Safety review toggle (admin/manager only)
  const handleSafetyReview = async (submissionId) => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.post(
        `${API}/forms/submissions/${submissionId}/safety-review`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.safety_reviewed ? 'Marked as reviewed' : 'Review cleared');
      fetchData();
    } catch (err) {
      console.error('Safety review failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  // Pay period math: 2-week periods, Sunday to Saturday.
  // Anchor: pay period Apr 12 - Apr 25, 2026 (Week 1 starts Sun Apr 12, 2026).
  const PAY_PERIOD_ANCHOR = new Date(2026, 3, 12); // April 12, 2026
  const getPayPeriodInfo = (dateStr) => {
    if (!dateStr) return null;
    // Parse YYYY-MM-DD as a local date (avoid UTC shift)
    let d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, day] = dateStr.split('-').map(Number);
      d = new Date(y, m - 1, day);
    } else {
      d = new Date(dateStr);
    }
    if (isNaN(d.getTime())) return null;
    const daysDiff = Math.floor((d - PAY_PERIOD_ANCHOR) / (1000 * 60 * 60 * 24));
    const inPeriodDay = ((daysDiff % 14) + 14) % 14;
    const weekLabel = inPeriodDay < 7 ? 'Week 1' : 'Week 2';
    return weekLabel;
  };

  // Get display fields based on form type
  const getDisplayFields = () => {
    if (!template) return [];
    const name = template.name.toLowerCase();
    
    if (name.includes('clearsky')) {
      return ['date', 'unit_number', 'diesel_added', 'clearsky_added'];
    } else if (name.includes('bill of lading') || name.includes('bol')) {
      return ['bol_number', 'date', 'truck_number', 'charge_to_name', 'cargo_description'];
    } else if (name.includes('flha')) {
      // FLHA template uses location_lsd + work_description
      return ['date', 'location_lsd', 'work_description', 'hazards_identified'];
    } else if (name.includes('hazard')) {
      // Hazard ID template now has Location, Job Description, Hazards Identified
      return ['date', 'location', 'job_description', 'hazards_identified'];
    } else if (name.includes('incident')) {
      return ['date', 'incident_type', 'location', 'description'];
    } else if (name.includes('inspection') || name.includes('site tour')) {
      return ['date', 'location', 'inspector_name'];
    } else if (name.includes('time-off')) {
      return ['operator_name', 'first_day_off', 'last_day_off', 'reason'];
    } else if (name.includes('timesheet') || name.includes('time sheet')) {
      const isShop = name.includes('shop');
      // Dynamically find fields from template so we work with ANY timesheet variant
      let hoursField = null;
      let jobField = null;
      let unitField = null;
      let dateField = null;
      let employeeField = null;  // single combined name field if present
      let firstNameField = null;
      let lastNameField = null;
      
      template.sections?.forEach(section => {
        section.fields?.forEach(field => {
          const label = (field.label || '').toLowerCase();
          const id = (field.id || '').toLowerCase();
          if (!hoursField && (label.includes('hour') || id.includes('hour'))) {
            hoursField = field.id;
          }
          if (!jobField && ((label.includes('job') && !label.includes('description')) || id.includes('job_number') || id === 'job')) {
            jobField = field.id;
          }
          if (!unitField && (label.includes('unit') || label.includes('truck') || id === 'unit' || id === 'unit_number')) {
            unitField = field.id;
          }
          if (!dateField && field.type === 'date') {
            dateField = field.id;
          }
          // Date by name fallback
          if (!dateField && (label === 'date' || id === 'date' || id.includes('work_date') || id.includes('shift_date') || id.includes('entry_date'))) {
            dateField = field.id;
          }
          // Combined employee name
          if (!employeeField && (
            id === 'employee_name' || id === 'name' || id === 'full_name' ||
            id === 'operator_name' || id === 'worker_name' || id === 'driver_name' ||
            (label.includes('employee') && label.includes('name')) ||
            (label.includes('worker') && label.includes('name')) ||
            (label === 'name' && !label.includes('first') && !label.includes('last'))
          )) {
            employeeField = field.id;
          }
          if (!firstNameField && (id === 'first_name' || label.includes('first name'))) {
            firstNameField = field.id;
          }
          if (!lastNameField && (id === 'last_name' || label.includes('last name'))) {
            lastNameField = field.id;
          }
        });
      });
      
      // Stash detected fields for later use by getFieldValue/getFieldLabel/employeeOptions
      // via a virtual "_employee" identifier
      template._detected_employee_field = employeeField;
      template._detected_first_name_field = firstNameField;
      template._detected_last_name_field = lastNameField;
      template._detected_date_field = dateField;
      
      const empCol = '_employee';
      const dateCol = dateField || '_date';
      const hoursCol = hoursField || 'hours';
      const unitCol = unitField || 'unit';
      const jobCol = jobField || 'job_number';
      
      if (isShop) {
        return [empCol, dateCol, hoursCol, unitCol];
      }
      return [empCol, dateCol, hoursCol, jobCol, unitCol];
    }
    
    // Default: get first 4 fields from template
    const fields = [];
    template.sections?.forEach(section => {
      section.fields?.forEach(field => {
        if (fields.length < 4 && field.type !== 'image' && field.type !== 'signature') {
          fields.push(field.id);
        }
      });
    });
    return fields;
  };

  const getFieldLabel = (fieldId) => {
    // Special case for computed/synthetic fields
    if (fieldId === 'employee_name' || fieldId === '_employee') return 'Employee';
    if (fieldId === '_date') return 'Date';
    
    if (!template) return fieldId;
    for (const section of template.sections || []) {
      for (const field of section.fields || []) {
        if (field.id === fieldId) {
          return field.label;
        }
      }
    }
    // Fallback formatting
    return fieldId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getFieldValue = (entry, fieldId) => {
    const data = entry.data || {};
    
    // Synthetic _employee — uses detected fields from template
    if (fieldId === '_employee' || fieldId === 'employee_name') {
      const empF = template?._detected_employee_field;
      const fnF = template?._detected_first_name_field;
      const lnF = template?._detected_last_name_field;
      const directName = empF ? data[empF] : null;
      const split = `${(fnF ? data[fnF] : data.first_name) || ''} ${(lnF ? data[lnF] : data.last_name) || ''}`.trim();
      const fallback = data.employee_name || data.name || data.operator_name 
                     || data.worker_name || data.driver_name || data.full_name 
                     || entry.submitted_by || '';
      return directName || split || fallback || '-';
    }
    
    // Synthetic _date — uses detected date field from template
    if (fieldId === '_date') {
      const df = template?._detected_date_field;
      if (df && data[df]) return data[df];
      return data.date || data.work_date || data.entry_date 
           || data.shift_date || data.shift_start_date || '-';
    }
    
    // Special fallback for `date` field — different templates may use different IDs
    if (fieldId === 'date') {
      const v = data.date || data.work_date || data.entry_date || data.shift_date || data.shift_start_date || '';
      return v || '-';
    }
    
    let value = data[fieldId];
    
    if (value === undefined || value === null || value === '') return '-';
    
    // Handle arrays
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    // Truncate long text
    if (typeof value === 'string' && value.length > 50) {
      return value.substring(0, 50) + '...';
    }
    
    return value;
  };

  // Helper to resolve the date value for an entry across timesheet variants
  const resolveEntryDate = (entry) => {
    const d = entry?.data || {};
    const detected = template?._detected_date_field;
    if (detected && d[detected]) return d[detected];
    // Direct literal keys first
    const literal = d.date || d.work_date || d.entry_date
        || d.shift_date || d.shift_start_date
        || d.first_day_off || d.date_of_request;
    if (literal) return literal;
    // Resolve UUID-keyed date fields by inspecting template field types/labels.
    // Templates rebuilt with UUID ids (Building Inspection, Observation, Time-Off,
    // Shop Time Sheet) store the date under data.{uuid}.
    if (template?.sections) {
      for (const sec of template.sections) {
        for (const f of (sec.fields || [])) {
          const fid = f.id;
          if (!fid) continue;
          const ftype = (f.type || '').toLowerCase();
          const label = (f.label || '').toLowerCase();
          const isDateish = ftype === 'date'
            || label === 'date'
            || label.includes('date')
            || label.includes('day off')
            || label.includes('shift')
            || label.includes('work day');
          if (isDateish && d[fid]) return d[fid];
        }
      }
    }
    return entry?.submitted_at || '';
  };
  
  // Helper to resolve the employee name for an entry
  const resolveEmployeeName = (entry) => {
    const d = entry?.data || {};
    const empF = template?._detected_employee_field;
    const fnF = template?._detected_first_name_field;
    const lnF = template?._detected_last_name_field;
    const direct = empF ? d[empF] : null;
    const split = `${(fnF ? d[fnF] : d.first_name) || ''} ${(lnF ? d[lnF] : d.last_name) || ''}`.trim();
    return direct || split || d.employee_name || d.name || d.operator_name 
        || d.worker_name || d.driver_name || d.full_name || entry?.submitted_by || '';
  };
  
  // Build employee dropdown options - prefer server-side full list, fall back to current entries
  // Sorted by LAST name (last whitespace-delimited word)
  const sortByLastName = (arr) => arr.slice().sort((a, b) => {
    const la = (a.trim().split(/\s+/).pop() || '').toLowerCase();
    const lb = (b.trim().split(/\s+/).pop() || '').toLowerCase();
    if (la < lb) return -1;
    if (la > lb) return 1;
    return a.toLowerCase().localeCompare(b.toLowerCase());
  });
  const employeeOptions = (() => {
    if (!isTimesheet) return [];
    if (allEmployees.length > 0) return sortByLastName(allEmployees);
    const names = new Set();
    entries.forEach(e => {
      const n = resolveEmployeeName(e);
      if (n) names.add(n);
    });
    return sortByLastName(Array.from(names));
  })();
  
  // Filter entries by search and date
  const filteredEntries = entries.filter(entry => {
    // Text search filter
    if (search) {
      const searchLower = search.toLowerCase();
      const data = entry.data || {};
      
      let matchesSearch = false;
      if ((entry.submitted_by || '').toLowerCase().includes(searchLower)) matchesSearch = true;
      
      for (const key of Object.keys(data)) {
        const val = data[key];
        if (typeof val === 'string' && val.toLowerCase().includes(searchLower)) {
          matchesSearch = true;
          break;
        }
      }
      
      if (!matchesSearch) return false;
    }
    
    // Employee + last-name filters now run server-side so we never re-filter here
    
    // Date filter — prefer the user-entered date over submitted_at
    if (dateFilter !== 'all') {
      const preferredDateStr = resolveEntryDate(entry);
      // Parse YYYY-MM-DD as a local date (not UTC) so tonight's UTC shift doesn't change the day
      let entryDate;
      if (typeof preferredDateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(preferredDateStr)) {
        const [y, m, day] = preferredDateStr.split('-').map(Number);
        entryDate = new Date(y, m - 1, day);
      } else {
        entryDate = new Date(preferredDateStr);
      }
      if (isNaN(entryDate.getTime())) return false;
      if (isNaN(entryDate.getTime())) return true; // Keep entries with invalid dates
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dateFilter === 'today') {
        const entryDay = new Date(entryDate);
        entryDay.setHours(0, 0, 0, 0);
        return entryDay.getTime() === today.getTime();
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return entryDate >= weekAgo;
      } else if (dateFilter === 'month') {
        if (selectedMonth && selectedYear) {
          const year = parseInt(selectedYear);
          const month = parseInt(selectedMonth) - 1;
          return entryDate.getFullYear() === year && entryDate.getMonth() === month;
        }
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return entryDate >= monthAgo;
      } else if (dateFilter === 'year') {
        if (selectedYear) {
          return entryDate.getFullYear() === parseInt(selectedYear);
        }
        const yearAgo = new Date(today);
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        return entryDate >= yearAgo;
      } else if (dateFilter === 'custom') {
        // Parse YYYY-MM-DD inputs as LOCAL dates so picking "2026-05-11" as the
        // end date actually includes May 11 in BC (not "May 11 UTC midnight" which
        // is May 10 17:00 BC — the cause of the off-by-one we saw on production).
        const parseLocal = (s) => {
          if (typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
            const [y, m, day] = s.split('-').map(Number);
            return new Date(y, m - 1, day);
          }
          return new Date(s);
        };
        if (customDateStart && customDateEnd) {
          const start = parseLocal(customDateStart);
          const end = parseLocal(customDateEnd);
          end.setHours(23, 59, 59, 999);
          return entryDate >= start && entryDate <= end;
        } else if (customDateStart) {
          const start = parseLocal(customDateStart);
          return entryDate >= start;
        } else if (customDateEnd) {
          const end = parseLocal(customDateEnd);
          end.setHours(23, 59, 59, 999);
          return entryDate <= end;
        }
      }
    }
    
    return true;
  });

  const clearDateFilter = () => {
    setDateFilter('all');
    setCustomDateStart('');
    setCustomDateEnd('');
    setSelectedMonth('');
    setSelectedYear('');
  };

  // Generate year options (last 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const displayFields = getDisplayFields();

  // Get category color
  const getCategoryColor = () => {
    if (!template) return 'blue';
    const category = (template.category || '').toLowerCase();
    if (category === 'safety') return 'red';
    if (category === 'logistics') return 'blue';
    if (category === 'operations') return 'emerald';
    return 'purple';
  };

  const color = getCategoryColor();

  // While we don't yet know the template name, render nothing.
  // Prevents a brief flash of the generic UI before redirects (e.g. ClearSky).
  if (!template) {
    return null;
  }

  // ClearSky has its own dedicated page with totals/variance — redirect to it
  // so the user always sees the same (richer) view.
  if (template?.name?.toLowerCase().includes('clearsky')) {
    return <Navigate to="/dashboard/clearsky-log" replace />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/forms">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Forms
            </Button>
          </Link>
          <div>
            <h1 className={`text-2xl font-bold text-foreground flex items-center gap-2`}>
              <FileText className={`h-6 w-6 text-${color}-500`} />
              {template?.name || 'Form'} Log
            </h1>
            <p className="text-muted-foreground text-sm">
              {template?.description || 'View all submissions'}
            </p>
          </div>
        </div>
        {templateId && (
          <Link to={`/dashboard/forms/fill/${templateId}`}>
            <Button className={`bg-${color}-600 hover:bg-${color}-700`}>
              <Plus className="h-4 w-4 mr-2" />
              New Entry
            </Button>
          </Link>
        )}
      </div>

      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={`border-${color}-500/20`}>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Total Entries</div>
            <div className="text-2xl font-bold text-foreground">{total}</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Showing</div>
            <div className="text-2xl font-bold text-foreground">{filteredEntries.length}</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Category</div>
            <div className="text-xl font-semibold text-foreground capitalize">
              {template?.category || 'General'}
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Last Submission</div>
            <div className="text-lg text-foreground">
              {entries[0] ? formatDateTime(entries[0].submitted_at) : 'No submissions'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Date Filter */}
      <Card>
        <CardContent className="p-4">
          {/* Debug banner for timesheet */}
          {isTimesheet && (
            <div className="mb-4 p-2 bg-yellow-500/20 border border-yellow-500/30 rounded text-sm">
              <strong>Debug:</strong> 
              Auth Loading: <code>{authLoading ? 'YES' : 'NO'}</code> | 
              Logged in as: <code>{currentUserEmail || 'not loaded'}</code> | 
              Form Type: <code>{getFormType() || 'unknown'}</code> |
              Can approve Admin: <strong>{canApproveAdmin() ? 'YES' : 'NO'}</strong> | 
              Can approve AR: <strong>{canApproveAR() ? 'YES' : 'NO'}</strong>
            </div>
          )}
          
          <div className="flex flex-wrap items-center gap-4">
            {/* Text Search */}
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search entries..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Last-name filter — available on every log */}
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter by last name..."
                value={lastNameFilter}
                onChange={(e) => setLastNameFilter(e.target.value)}
                className="w-[200px]"
                data-testid="lastname-filter"
              />
              {lastNameFilter && (
                <Button variant="ghost" size="sm" onClick={() => setLastNameFilter('')} data-testid="clear-lastname-filter">
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>

            {/* Employee Filter — only for timesheet pages, admin/manager only */}
            {isTimesheet && isAdminOrManager && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <Select value={employeeFilter || '_all_'} onValueChange={(v) => setEmployeeFilter(v === '_all_' ? '' : v)}>
                  <SelectTrigger className="w-[200px]" data-testid="employee-filter">
                    <SelectValue placeholder="All employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">All employees</SelectItem>
                    {employeeOptions.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date Filter Type */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Date filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">By Month</SelectItem>
                  <SelectItem value="year">By Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Month/Year selectors for month filter */}
            {dateFilter === 'month' && (
              <>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">January</SelectItem>
                    <SelectItem value="2">February</SelectItem>
                    <SelectItem value="3">March</SelectItem>
                    <SelectItem value="4">April</SelectItem>
                    <SelectItem value="5">May</SelectItem>
                    <SelectItem value="6">June</SelectItem>
                    <SelectItem value="7">July</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">September</SelectItem>
                    <SelectItem value="10">October</SelectItem>
                    <SelectItem value="11">November</SelectItem>
                    <SelectItem value="12">December</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            {/* Year selector for year filter */}
            {dateFilter === 'year' && (
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Custom date range */}
            {dateFilter === 'custom' && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">From:</span>
                  <Input
                    type="date"
                    value={customDateStart}
                    onChange={(e) => setCustomDateStart(e.target.value)}
                    className="w-[150px]"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">To:</span>
                  <Input
                    type="date"
                    value={customDateEnd}
                    onChange={(e) => setCustomDateEnd(e.target.value)}
                    className="w-[150px]"
                  />
                </div>
              </>
            )}

            {/* Clear filter button */}
            {dateFilter !== 'all' && (
              <Button variant="ghost" size="sm" onClick={clearDateFilter}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}

            <div className="text-sm text-muted-foreground ml-auto">
              {filteredEntries.length} of {total} entries
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entries Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Submitted
                    </div>
                  </th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {isTimesheet ? 'Employee' : 'By'}
                    </div>
                  </th>
                  {displayFields.map(fieldId => (
                    <th key={fieldId} className="text-left p-4 text-sm font-medium text-muted-foreground">
                      {getFieldLabel(fieldId)}
                    </th>
                  ))}
                  {isTimesheet && (
                    <th className="text-center p-4 text-sm font-medium text-muted-foreground">Pay Period</th>
                  )}
                  {isTimesheet && isAdminOrManager && (
                    <>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">Admin</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">AR</th>
                    </>
                  )}
                  {showReviewCheckbox && isAdminOrManager && (
                    <th className="text-center p-4 text-sm font-medium text-muted-foreground" title="Reviewed by safety person">Reviewed</th>
                  )}
                  {isTimeOff && (
                    <th className="text-center p-4 text-sm font-medium text-muted-foreground">Status</th>
                  )}
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={displayFields.length + (isTimesheet && isAdminOrManager ? 5 : 3) + (isTimesheet ? 1 : 0) + (isTimeOff ? 1 : 0) + (showReviewCheckbox && isAdminOrManager ? 1 : 0)} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                ) : filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan={displayFields.length + (isTimesheet && isAdminOrManager ? 5 : 3) + (isTimesheet ? 1 : 0) + (isTimeOff ? 1 : 0) + (showReviewCheckbox && isAdminOrManager ? 1 : 0)} className="text-center py-8 text-muted-foreground">
                      No entries found
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((entry) => (
                    <tr key={entry.id} className="border-b border-border/50 hover:bg-secondary/30">
                      <td className="p-4 text-sm text-muted-foreground">
                        {formatDateTime(entry.submitted_at)}
                      </td>
                      <td className="p-4 text-sm font-medium">{isTimesheet ? resolveEmployeeName(entry) : entry.submitted_by}</td>
                      {displayFields.map(fieldId => (
                        <td key={fieldId} className="p-4 text-sm">
                          {getFieldValue(entry, fieldId)}
                        </td>
                      ))}
                      {isTimesheet && (
                        <td className="p-4 text-center text-sm">
                          {(() => {
                            const wk = getPayPeriodInfo(resolveEntryDate(entry));
                            if (!wk) return <span className="text-muted-foreground">-</span>;
                            const isWeek1 = wk === 'Week 1';
                            return (
                              <span className={`inline-block px-2 py-1 rounded font-medium text-xs ${
                                isWeek1
                                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40'
                                  : 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                              }`}>
                                {wk}
                              </span>
                            );
                          })()}
                        </td>
                      )}
                      {isTimesheet && isAdminOrManager && (
                        <>
                          {/* Admin Approval Column */}
                          <td className="p-4 text-center align-middle">
                            <button
                              type="button"
                              disabled={authLoading || !canApproveAdmin()}
                              onClick={() => {
                                if (entry.approval_admin?.approved) {
                                  handleRevokeApproval(entry.id, 'admin');
                                } else {
                                  handleApprove(entry.id, 'admin');
                                }
                              }}
                              className={`inline-flex items-center justify-center h-6 w-6 rounded border-2 transition-colors ${
                                entry.approval_admin?.approved
                                  ? 'bg-green-600 border-green-600 text-white'
                                  : 'bg-transparent border-muted-foreground/40 text-transparent'
                              } ${(authLoading || !canApproveAdmin()) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-green-500'}`}
                              data-testid={`admin-approval-${entry.id}`}
                              title={entry.approval_admin?.approved ? `Approved by ${entry.approval_admin?.initials || ''}` : 'Not approved'}
                            >
                              <Check className="h-4 w-4" strokeWidth={3} />
                            </button>
                          </td>
                          {/* AR Approval Column */}
                          <td className="p-4 text-center align-middle">
                            <button
                              type="button"
                              disabled={authLoading || !canApproveAR()}
                              onClick={() => {
                                if (entry.approval_ar?.approved) {
                                  handleRevokeApproval(entry.id, 'ar');
                                } else {
                                  handleApprove(entry.id, 'ar');
                                }
                              }}
                              className={`inline-flex items-center justify-center h-6 w-6 rounded border-2 transition-colors ${
                                entry.approval_ar?.approved
                                  ? 'bg-green-600 border-green-600 text-white'
                                  : 'bg-transparent border-muted-foreground/40 text-transparent'
                              } ${(authLoading || !canApproveAR()) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-green-500'}`}
                              data-testid={`ar-approval-${entry.id}`}
                              title={entry.approval_ar?.approved ? `Approved by ${entry.approval_ar?.initials || ''}` : 'Not approved'}
                            >
                              <Check className="h-4 w-4" strokeWidth={3} />
                            </button>
                          </td>
                        </>
                      )}
                      {showReviewCheckbox && isAdminOrManager && (
                        <td className="p-4 text-center align-middle">
                          <button
                            type="button"
                            onClick={() => handleSafetyReview(entry.id)}
                            className={`inline-flex items-center justify-center h-6 w-6 rounded border-2 transition-colors cursor-pointer ${
                              entry.safety_reviewed
                                ? 'bg-green-600 border-green-600 text-white'
                                : 'bg-transparent border-muted-foreground/40 text-transparent hover:border-green-500'
                            }`}
                            data-testid={`safety-review-${entry.id}`}
                            title={entry.safety_reviewed ? `Reviewed by ${entry.safety_reviewed_by || ''}` : 'Mark as reviewed'}
                          >
                            <Check className="h-4 w-4" strokeWidth={3} />
                          </button>
                        </td>
                      )}
                      {isTimeOff && (
                        <td className="p-4 text-center align-middle">
                          {isAdmin ? (
                            <div className="flex justify-center gap-1">
                              <button
                                onClick={() => handleTimeOffDecision(entry.id, 'approve')}
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  entry.time_off_status === 'approved'
                                    ? 'bg-green-500 text-white'
                                    : 'bg-muted/30 text-green-400 hover:bg-green-500/20'
                                }`}
                                data-testid={`time-off-approve-${entry.id}`}
                                title="Approve"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => handleTimeOffDecision(entry.id, 'deny')}
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  entry.time_off_status === 'denied'
                                    ? 'bg-red-500 text-white'
                                    : 'bg-muted/30 text-red-400 hover:bg-red-500/20'
                                }`}
                                data-testid={`time-off-deny-${entry.id}`}
                                title="Deny"
                              >
                                ✗
                              </button>
                            </div>
                          ) : (
                            <span className={`text-xs font-medium ${
                              entry.time_off_status === 'approved' ? 'text-green-400' :
                              entry.time_off_status === 'denied' ? 'text-red-400' : 'text-muted-foreground'
                            }`}>
                              {entry.time_off_status ? entry.time_off_status.charAt(0).toUpperCase() + entry.time_off_status.slice(1) : 'Pending'}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`/dashboard/forms/view/${entry.id}`}>
                            <Button variant="ghost" size="sm" title="View">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Link to={`/dashboard/forms/edit/${entry.id}`}>
                            <Button variant="ghost" size="sm" title="Edit" data-testid={`edit-entry-${entry.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Link>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              onClick={() => handleDeleteSubmission(entry.id)}
                              data-testid={`delete-entry-${entry.id}`}
                              title="Delete entry"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {isTimesheet && filteredEntries.length > 0 && (() => {
                // Find which template field is "hours"
                let hoursField = 'hours';
                template?.sections?.forEach(section => {
                  section.fields?.forEach(field => {
                    const label = (field.label || '').toLowerCase();
                    const id = field.id || '';
                    if (label.includes('hour') || id.includes('hour')) {
                      hoursField = field.id;
                    }
                  });
                });
                const total = filteredEntries.reduce((sum, e) => {
                  const v = parseFloat(e.data?.[hoursField] || 0);
                  return sum + (isNaN(v) ? 0 : v);
                }, 0);
                const week1 = filteredEntries.filter(e => getPayPeriodInfo(resolveEntryDate(e)) === 'Week 1')
                  .reduce((sum, e) => sum + (parseFloat(e.data?.[hoursField] || 0) || 0), 0);
                const week2 = filteredEntries.filter(e => getPayPeriodInfo(resolveEntryDate(e)) === 'Week 2')
                  .reduce((sum, e) => sum + (parseFloat(e.data?.[hoursField] || 0) || 0), 0);
                return (
                  <tfoot className="bg-muted/40 border-t-2 border-border">
                    <tr>
                      <td colSpan={displayFields.length + 2} className="p-3 text-sm font-semibold text-right">
                        Total Hours ({filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'})
                      </td>
                      <td className="p-3 text-center" colSpan={1 + (isTimesheet && isAdminOrManager ? 2 : 0) + (isTimeOff ? 1 : 0) + 1}>
                        <div className="flex items-center justify-center gap-3 text-sm font-bold">
                          <span className="text-cyan-400">W1: {week1.toFixed(1)}</span>
                          <span className="text-amber-400">W2: {week2.toFixed(1)}</span>
                          <span className="text-foreground">Total: {total.toFixed(1)}</span>
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
